// Word-export för de två projektörsdokument som saknade generator:
// Dok 10 – Arbetsområdesutredning och Dok 16 – Projektörsinlämning.
import type { Paragraph, Table } from "docx";
import { downloadWordDocument } from "@/lib/documentGenerator/exportHistory";
import { createStampForCurrentUser } from "@/lib/documentGenerator/watermark";
import { body, buildDocument, factTable, h1, table } from "@/lib/documentGenerator/docTheme";
import { WORK_AREA_STATUS_LABELS, type WorkAreaSurveyStatus } from "@/lib/basProcess/constants";
import { documentFooter } from "@/lib/documentGenerator/documentFooter";

/* ---------- Dok 10 – Arbetsområdesutredning ---------- */

export interface WorkAreaSurveyItemDoc {
  category: string;
  control_point: string;
  legal_reference: string | null;
  status: string;
  comment: string | null;
  responsible: string | null;
}

export interface WorkAreaSurveyDoc {
  area_name: string;
  delimitation: string | null;
  adjacent_activity: string | null;
  notes: string | null;
  status: string;
  items: WorkAreaSurveyItemDoc[];
}

export const generateWorkAreaSurveyWordDocument = async (
  survey: WorkAreaSurveyDoc,
  projectName?: string
) => {
  const documentName = `Arbetsområdesutredning – ${survey.area_name}`;
  const stamp = await createStampForCurrentUser({
    docNumber: "10",
    documentName,
    projectName,
  });

  const children: (Paragraph | Table)[] = [
    h1("1. Områdesfakta"),
    factTable([
      ["Område", survey.area_name],
      ["Avgränsning/gränsdragning", survey.delimitation],
      ["Angränsande verksamhet", survey.adjacent_activity],
      ["Anteckningar", survey.notes],
    ]),
  ];

  const categories = Array.from(new Set(survey.items.map((i) => i.category)));
  categories.forEach((category, index) => {
    children.push(
      h1(`${index + 2}. ${category}`),
      table(
        ["Kontrollpunkt", "Status", "Ansvarig", "Kommentar"],
        survey.items
          .filter((i) => i.category === category)
          .map((i) => [
            i.legal_reference ? `${i.control_point}\n(${i.legal_reference})` : i.control_point,
            WORK_AREA_STATUS_LABELS[i.status as WorkAreaSurveyStatus] ?? i.status,
            i.responsible,
            i.comment,
          ]),
        { columnWidths: [4000, 1500, 1500, 2360] }
      )
    );
  });

  const footer = await documentFooter({
    docNumber: "10",
    documentType: "arbetsomrade",
  });

  const doc = buildDocument({
    meta: {
      review: footer.review,
      version: footer.version,
      docNumber: "10",
      documentName: "Arbetsområdesutredning",
      projectName,
      status: survey.status,
      extra: [["Område", survey.area_name]],
    },
    stamp,
    children: [...children, ...footer.children],
  });

  await downloadWordDocument(
    doc,
    `Dok10_Arbetsomradesutredning_${survey.area_name.replace(/\s+/g, "_")}.docx`,
    {
      documentType: "arbetsomrade",
      docNumber: "10",
      documentName,
      snapshot: survey,
      stamp,
    }
  );
};

/* ---------- Dok 16 – Projektörsinlämning ---------- */

export interface DesignerSubmissionDoc {
  discipline: string;
  company: string | null;
  contact_name: string | null;
  contact_email: string | null;
  status: string;
  residual_risks_construction: string | null;
  residual_risks_use: string | null;
  assumptions: string | null;
  basp_comment: string | null;
  submitted_at: string | null;
}

const SUBMISSION_STATUS_LABELS: Record<string, string> = {
  draft: "Utkast",
  submitted: "Inlämnad",
  needs_completion: "Behöver kompletteras",
  accepted: "Godkänd av Bas-P",
};

export const generateDesignerSubmissionWordDocument = async (
  submission: DesignerSubmissionDoc,
  projectName?: string
) => {
  const documentName = `Projektörsinlämning – ${submission.discipline}`;
  const stamp = await createStampForCurrentUser({
    docNumber: "16",
    documentName,
    projectName,
  });

  const children: (Paragraph | Table)[] = [
    h1("1. Uppgifter"),
    factTable([
      ["Disciplin", submission.discipline],
      ["Företag", submission.company],
      ["Kontaktperson", submission.contact_name],
      ["E-post", submission.contact_email],
      ["Status", SUBMISSION_STATUS_LABELS[submission.status] ?? submission.status],
    ]),
    h1("2. Kvarvarande risker – byggskede"),
    body(submission.residual_risks_construction ?? "Inga kvarvarande risker redovisade."),
    h1("3. Kvarvarande risker – bruksskede"),
    body(submission.residual_risks_use ?? "Inga kvarvarande risker redovisade."),
    h1("4. Förutsättningar och antaganden"),
    body(submission.assumptions ?? "–"),
  ];

  if (submission.basp_comment) {
    children.push(h1("5. Kommentar från Bas-P"), body(submission.basp_comment));
  }

  const footer = await documentFooter({
    docNumber: "16",
    documentType: "projektorinlamning",
    signatureRoles: ["Projektör", "Bas-P"],
  });

  const doc = buildDocument({
    meta: {
      review: footer.review,
      version: footer.version,
      docNumber: "16",
      documentName: "Projektörsinlämning – kvarvarande risker",
      projectName,
      status: SUBMISSION_STATUS_LABELS[submission.status] ?? submission.status,
      extra: [["Disciplin", submission.discipline]],
    },
    stamp,
    children: [...children, ...footer.children],
  });

  await downloadWordDocument(
    doc,
    `Dok16_Projektorsinlamning_${submission.discipline.replace(/[^\w-]+/g, "_")}.docx`,
    {
      documentType: "projektorinlamning",
      docNumber: "16",
      documentName,
      snapshot: submission,
      stamp,
    }
  );
};
